import { useState, type FormEvent } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Pencil, Trash2 } from 'lucide-react';

import {
  commentsQueryKey,
  createComment,
  deleteComment,
  editComment,
  isTombstone,
  listThread,
  type CommentEntityType,
  type CommentView,
} from '@/api/comms/comments.api';
import { Button } from '@/components/ui/Button';
import { hasCapability, isSuperadmin } from '@/lib/capabilities';
import { useAuth } from '@/hooks/useAuth';
import { useToast } from '@/hooks/useToast';

/**
 * Self-fetching, lazy comment thread on an EOS rock / to-do / issue
 * (Stream E, ADR-027 §7.3). It only queries once mounted — the host
 * pages mount it inside an expanded drawer or an open modal, so opening
 * the thread is what triggers the fetch. No polling: the thread refetches
 * on open and after each mutation.
 *
 * Threads are org-transparent. Any member posts; authors edit their own
 * (an "edited" chip appears); deletes are soft — author, or a
 * `manage_eos`/superadmin moderator — and survive as "Comment removed"
 * tombstones so later replies keep their context. The server re-enforces
 * every rule; the gating here is UI affordance only.
 */

/** Compact relative time — "just now", "5m ago", … falling back to a date. */
function relativeTime(iso: string): string {
  const then = new Date(iso).getTime();
  if (Number.isNaN(then)) return '';
  const sec = Math.round((Date.now() - then) / 1000);
  if (sec < 45) return 'just now';
  const min = Math.round(sec / 60);
  if (min < 60) return `${String(min)}m ago`;
  const hr = Math.round(min / 60);
  if (hr < 24) return `${String(hr)}h ago`;
  const day = Math.round(hr / 24);
  if (day < 7) return `${String(day)}d ago`;
  return new Date(iso).toLocaleDateString();
}

const TEXTAREA_CLS =
  'w-full rounded-kw-md border border-kw-border bg-white px-3 py-2 text-[13px] text-kw-shark placeholder:text-kw-muted focus:border-kw-cerulean focus:outline-none focus:ring-2 focus:ring-kw-cerulean/20';

export function CommentThread({ entityType, entityId }: { entityType: CommentEntityType; entityId: string }) {
  const { state: authState } = useAuth();
  const queryClient = useQueryClient();
  const toast = useToast();

  const me = authState.status === 'authenticated' ? authState.me : null;
  const myMembershipId = me?.membership.id ?? null;
  const isModerator = me !== null && (isSuperadmin(me) || hasCapability(me, 'manage_eos'));

  const [draft, setDraft] = useState('');
  const [editingId, setEditingId] = useState<string | null>(null);

  const queryKey = commentsQueryKey(entityType, entityId);
  const threadQuery = useQuery({
    queryKey,
    queryFn: () => listThread({ entityType, entityId }),
  });

  function invalidate(): void {
    void queryClient.invalidateQueries({ queryKey });
  }

  const postMutation = useMutation({
    mutationFn: (bodyText: string) => createComment({ entityType, entityId, bodyText }),
    onSuccess: () => {
      setDraft('');
      invalidate();
    },
    onError: () => {
      toast.show('Could not post your comment.', { variant: 'danger' });
    },
  });

  const editMutation = useMutation({
    mutationFn: ({ id, bodyText }: { id: string; bodyText: string }) => editComment(id, { bodyText }),
    onSuccess: () => {
      setEditingId(null);
      invalidate();
    },
    onError: () => {
      toast.show('Could not save your edit.', { variant: 'danger' });
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => deleteComment(id),
    onSuccess: invalidate,
    onError: () => {
      toast.show('Could not delete the comment.', { variant: 'danger' });
    },
  });

  function handlePost(e: FormEvent<HTMLFormElement>): void {
    e.preventDefault();
    const body = draft.trim();
    if (!body || postMutation.isPending) return;
    postMutation.mutate(body);
  }

  const items = threadQuery.data ?? [];

  return (
    <div className="space-y-3">
      {threadQuery.isLoading ? (
        <p className="text-[12px] text-kw-muted">Loading discussion…</p>
      ) : threadQuery.error ? (
        <p className="text-[12px] text-kw-danger-text">Couldn&apos;t load the discussion.</p>
      ) : items.length === 0 ? (
        <p className="text-[12px] text-kw-muted">No comments yet — start the discussion.</p>
      ) : (
        <ul className="space-y-3">
          {items.map((item) =>
            isTombstone(item) ? (
              <li key={item.id} className="text-[12px] italic text-kw-ghost">
                Comment removed
              </li>
            ) : (
              <CommentRow
                key={item.id}
                comment={item}
                isEditing={editingId === item.id}
                canEdit={myMembershipId !== null && item.authorMembershipId === myMembershipId}
                canDelete={
                  isModerator || (myMembershipId !== null && item.authorMembershipId === myMembershipId)
                }
                isSaving={editMutation.isPending}
                isDeleting={deleteMutation.isPending}
                onStartEdit={() => {
                  setEditingId(item.id);
                }}
                onCancelEdit={() => {
                  setEditingId(null);
                }}
                onSaveEdit={(bodyText) => {
                  editMutation.mutate({ id: item.id, bodyText });
                }}
                onDelete={() => {
                  if (window.confirm('Delete this comment? This cannot be undone.')) {
                    deleteMutation.mutate(item.id);
                  }
                }}
              />
            ),
          )}
        </ul>
      )}

      <form onSubmit={handlePost} className="space-y-2">
        <textarea
          value={draft}
          onChange={(e) => {
            setDraft(e.target.value);
          }}
          rows={2}
          maxLength={4000}
          placeholder="Add a comment…"
          aria-label="Add a comment"
          className={TEXTAREA_CLS}
        />
        <div className="flex justify-end">
          <Button
            type="submit"
            size="sm"
            variant="secondary"
            isLoading={postMutation.isPending}
            disabled={!draft.trim() || postMutation.isPending}
          >
            Comment
          </Button>
        </div>
      </form>
    </div>
  );
}

function CommentRow({
  comment,
  isEditing,
  canEdit,
  canDelete,
  isSaving,
  isDeleting,
  onStartEdit,
  onCancelEdit,
  onSaveEdit,
  onDelete,
}: {
  comment: CommentView;
  isEditing: boolean;
  canEdit: boolean;
  canDelete: boolean;
  isSaving: boolean;
  isDeleting: boolean;
  onStartEdit: () => void;
  onCancelEdit: () => void;
  onSaveEdit: (bodyText: string) => void;
  onDelete: () => void;
}) {
  const [value, setValue] = useState(comment.bodyText);

  return (
    <li className="space-y-1">
      <div className="flex items-center gap-2 text-[12px] text-kw-muted">
        <span className="font-medium text-kw-shark">{comment.authorNameSnapshot || 'Former teammate'}</span>
        <span>· {relativeTime(comment.createdAt)}</span>
        {comment.editedAt ? <span className="italic">· edited</span> : null}
        {canEdit || canDelete ? (
          <span className="ml-auto flex items-center gap-1">
            {canEdit && !isEditing ? (
              <button
                type="button"
                aria-label="Edit comment"
                onClick={onStartEdit}
                className="rounded p-0.5 text-kw-muted hover:bg-kw-bg hover:text-kw-shark"
              >
                <Pencil className="size-3.5" />
              </button>
            ) : null}
            {canDelete ? (
              <button
                type="button"
                aria-label="Delete comment"
                disabled={isDeleting}
                onClick={onDelete}
                className="rounded p-0.5 text-kw-muted hover:bg-kw-bg hover:text-kw-danger-text disabled:opacity-40"
              >
                <Trash2 className="size-3.5" />
              </button>
            ) : null}
          </span>
        ) : null}
      </div>

      {isEditing ? (
        <form
          onSubmit={(e: FormEvent<HTMLFormElement>) => {
            e.preventDefault();
            const next = value.trim();
            if (next) onSaveEdit(next);
          }}
          className="space-y-2"
        >
          <textarea
            value={value}
            onChange={(e) => {
              setValue(e.target.value);
            }}
            rows={2}
            maxLength={4000}
            aria-label="Edit comment"
            className={TEXTAREA_CLS}
          />
          <div className="flex justify-end gap-2">
            <Button
              type="button"
              size="sm"
              variant="ghost"
              onClick={() => {
                setValue(comment.bodyText);
                onCancelEdit();
              }}
            >
              Cancel
            </Button>
            <Button type="submit" size="sm" isLoading={isSaving} disabled={!value.trim() || isSaving}>
              Save
            </Button>
          </div>
        </form>
      ) : (
        <p className="whitespace-pre-wrap text-[13px] text-kw-shark">{comment.bodyText}</p>
      )}
    </li>
  );
}
